import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  CampaignMarginPeriod,
  CreateAssociatedCampaignMarginRequest,
  MarginTimelinePeriod,
  usePreviewAssociatedCampaignMarginMutation,
} from "../../store/apis/associatedCampaignMarginApi";
import { formatPercentageNoMultiply } from "../../utils/formatUtils";
import { fmtMoneyDetailed } from "../../utils/format";
import {
  averageMarginIfEdited,
  forecastForRange,
  GoalFinancials,
  marginForPlatformInversion,
  platformInversionForAverageMargin,
  platformUnitCostForMargin,
} from "../../utils/marginForecast";

interface AssociatedCampaignMarginDialogProps {
  open: boolean;
  onClose: () => void;
  // El padre ejecuta create/update y propaga isLoading + serverError.
  onSubmit: (data: CreateAssociatedCampaignMarginRequest) => void;
  // Tramo a editar; si no se pasa, es modo "crear".
  period?: CampaignMarginPeriod | null;
  isLoading: boolean;
  serverError?: string | null;
  // Rango de fechas de la línea (para validar y acotar los DatePicker).
  campaignStartDate?: string | null;
  campaignEndDate?: string | null;
  // Para llamar al endpoint de preview.
  briefingId: string;
  associatedId: string;
  // Para calcular en vivo la inversión y el coste unitario plataforma.
  goal?: GoalFinancials | null;
  // Margen actual de cada día (YYYY-MM-DD → %).
  dailyMargins?: Map<string, number>;
}

const normalizeDateString = (date?: string | null): string => {
  if (!date) return "";
  const [datePart] = date.split("T");
  return datePart;
};

const MiniTimeline: React.FC<{
  title: string;
  periods: MarginTimelinePeriod[];
}> = ({ title, periods }) => {
  const theme = useTheme();
  return (
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
        {title}
      </Typography>
      <Stack spacing={0.5}>
        {periods.length === 0 ? (
          <Typography variant="caption" color={theme.palette.neutral[600]}>
            —
          </Typography>
        ) : (
          periods.map((p, idx) => (
            <Box
              key={`${p.startDate}-${p.endDate}-${idx}`}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                bgcolor: theme.palette.neutral[100],
              }}
            >
              <Typography variant="caption">
                {dayjs(p.startDate).format("YYYY-MM-DD")} →{" "}
                {dayjs(p.endDate).format("YYYY-MM-DD")}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {formatPercentageNoMultiply(p.margin)}
              </Typography>
            </Box>
          ))
        )}
      </Stack>
    </Box>
  );
};

const AssociatedCampaignMarginDialog: React.FC<
  AssociatedCampaignMarginDialogProps
> = ({
  open,
  onClose,
  onSubmit,
  period,
  isLoading,
  serverError,
  campaignStartDate,
  campaignEndDate,
  briefingId,
  associatedId,
  goal,
  dailyMargins,
}) => {
  const { t } = useTranslation();
  const [marginValue, setMarginValue] = useState<string>("");
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const [description, setDescription] = useState<string>("");
  // Texto de la inversión mientras se escribe; null = se muestra la calculada.
  const [investmentDraft, setInvestmentDraft] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    margin?: string;
    startDate?: string;
    endDate?: string;
  }>({});

  const [preview, { data: previewResp, isLoading: isPreviewing, reset }] =
    usePreviewAssociatedCampaignMarginMutation();

  const minDate = useMemo(
    () =>
      campaignStartDate
        ? dayjs(normalizeDateString(campaignStartDate))
        : undefined,
    [campaignStartDate]
  );
  const maxDate = useMemo(
    () =>
      campaignEndDate ? dayjs(normalizeDateString(campaignEndDate)) : undefined,
    [campaignEndDate]
  );

  // Inicializa el formulario SOLO al abrir el diálogo o al cambiar el tramo
  // que se edita. Importante: no depender de `reset` ni de la respuesta del
  // preview aquí; si no, cada vez que llega el preview se re-ejecutaría este
  // efecto y borraría lo que el usuario ha escrito.
  useEffect(() => {
    if (!open) return;
    if (period) {
      setMarginValue(String(period.margin));
      setStartDate(dayjs(normalizeDateString(period.startDate)));
      setEndDate(dayjs(normalizeDateString(period.endDate)));
      setDescription(period.description ?? "");
    } else {
      // Por defecto, desde mañana hasta el fin de la línea: el caso habitual
      // de reajustar el margen de lo que queda de campaña.
      const tomorrow = dayjs().add(1, "day").startOf("day");
      const start = minDate && tomorrow.isBefore(minDate) ? minDate : tomorrow;
      const fitsCampaign = !maxDate || !start.isAfter(maxDate);
      setMarginValue("");
      setStartDate(fitsCampaign ? start : null);
      setEndDate(fitsCampaign ? (maxDate ?? null) : null);
      setDescription("");
    }
    setInvestmentDraft(null);
    setErrors({});
    // Limpia cualquier preview previo al (re)abrir; no se llama en cada
    // resolución del preview porque este efecto solo depende de open/period.id.
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, period?.id]);

  const marginNum = parseFloat(marginValue);

  // Días que cambia el tramo frente a los que conservan su margen actual.
  const rangeForecast = useMemo(() => {
    if (!goal || !dailyMargins || dailyMargins.size === 0) return null;
    if (!startDate || !endDate || endDate.isBefore(startDate)) return null;
    return forecastForRange(
      dailyMargins,
      startDate.format("YYYY-MM-DD"),
      endDate.format("YYYY-MM-DD")
    );
  }, [goal, dailyMargins, startDate, endDate]);

  const validMargin =
    marginValue.trim() !== "" &&
    !isNaN(marginNum) &&
    marginNum >= 0 &&
    marginNum <= 100;

  const impact =
    goal && rangeForecast && validMargin
      ? {
          platformInversion: platformInversionForAverageMargin(
            goal,
            averageMarginIfEdited(rangeForecast, marginNum)
          ),
          netUnitCost: platformUnitCostForMargin(goal, marginNum),
        }
      : null;

  // Escribir la inversión despeja el margen que la produce.
  const [investmentError, setInvestmentError] = useState<string | null>(null);
  const handleInvestmentChange = (value: string): void => {
    setInvestmentDraft(value);
    setInvestmentError(null);
    if (!goal || !rangeForecast) return;
    const target = parseFloat(value.replace(",", "."));
    if (!Number.isFinite(target)) return;
    const solved = marginForPlatformInversion(goal, rangeForecast, target);
    if (solved === null) return;
    if (solved < 0 || solved > 100) {
      setInvestmentError(
        t(
          "briefingsView.marginSection.investmentOutOfRange",
          "Para esa inversión el margen tendría que ser {{margin}}",
          { margin: formatPercentageNoMultiply(solved) }
        )
      );
      return;
    }
    setMarginValue(String(Math.round(solved * 100) / 100));
  };
  const formValid =
    marginValue.trim() !== "" &&
    !isNaN(marginNum) &&
    marginNum >= 0 &&
    marginNum <= 100 &&
    startDate != null &&
    endDate != null &&
    !endDate.startOf("day").isBefore(startDate.startOf("day"));

  // Preview en vivo (debounced) cuando el formulario es válido.
  useEffect(() => {
    if (!open || !formValid || startDate == null || endDate == null) return;
    const handle = setTimeout(() => {
      preview({
        params: { briefingId, associatedId },
        body: {
          startDate: startDate.format("YYYY-MM-DD"),
          endDate: endDate.format("YYYY-MM-DD"),
          margin: marginNum,
        },
      });
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    formValid,
    marginValue,
    startDate,
    endDate,
    briefingId,
    associatedId,
  ]);

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};
    if (!marginValue.trim()) {
      newErrors.margin = t("common.required", "Required");
    } else if (isNaN(marginNum) || marginNum < 0 || marginNum > 100) {
      newErrors.margin = t(
        "briefingsView.marginSection.marginRange",
        "Margin must be between 0 and 100"
      );
    }
    if (!startDate) newErrors.startDate = t("common.required", "Required");
    if (!endDate) {
      newErrors.endDate = t("common.required", "Required");
    } else if (
      startDate &&
      endDate.startOf("day").isBefore(startDate.startOf("day"))
    ) {
      newErrors.endDate = t(
        "common.endDateAfterStart",
        "End date must be after or equal to start date"
      );
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (): void => {
    if (!validateForm() || !startDate || !endDate) return;
    onSubmit({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
      margin: marginNum,
      description: description.trim() === "" ? null : description.trim(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {period
          ? t("briefingsView.marginSection.editPeriod", "Edit margin period")
          : t("briefingsView.marginSection.addPeriod", "Add margin period")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {serverError && <Alert severity="error">{serverError}</Alert>}

          <TextField
            label={t("metrics.marginTable.margin", "Margin") + " (%)"}
            type="number"
            value={marginValue}
            onChange={(e) => {
              setMarginValue(e.target.value);
              setInvestmentDraft(null);
              setInvestmentError(null);
            }}
            fullWidth
            inputProps={{ step: "0.01", min: 0, max: 100 }}
            error={!!errors.margin}
            helperText={errors.margin}
          />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <DatePicker
                label={t("metrics.marginTable.startDate", "Start Date")}
                value={startDate}
                onChange={(newDate: dayjs.Dayjs | null) =>
                  setStartDate(newDate)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                  />
                )}
                minDate={minDate}
                maxDate={maxDate}
                disableMaskedInput
              />
              <DatePicker
                label={t("metrics.marginTable.endDate", "End Date")}
                value={endDate}
                onChange={(newDate: dayjs.Dayjs | null) => setEndDate(newDate)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    error={!!errors.endDate}
                    helperText={errors.endDate}
                  />
                )}
                minDate={startDate ?? minDate}
                maxDate={maxDate}
                disableMaskedInput
              />
            </Stack>
          </LocalizationProvider>

          <TextField
            label={t("briefingsView.marginSection.description", "Description")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />

          {goal && rangeForecast && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label={t(
                  "briefingsView.marginSection.platformInversion",
                  "Inversión plataforma prevista"
                )}
                value={
                  investmentDraft ??
                  (impact ? impact.platformInversion.toFixed(2) : "")
                }
                onChange={(e) => handleInvestmentChange(e.target.value)}
                onBlur={() => setInvestmentDraft(null)}
                inputProps={{ inputMode: "decimal" }}
                error={!!investmentError}
                helperText={
                  investmentError ??
                  t(
                    "briefingsView.marginSection.platformInversionEditHint",
                    "Toda la campaña. Plan: {{plan}}. Escríbela para calcular el margen.",
                    { plan: fmtMoneyDetailed(goal.platformInversion, 2) }
                  )
                }
                fullWidth
              />
              <TextField
                label={t(
                  "briefingsView.marginSection.platformUnitCost",
                  "Coste unitario plataforma"
                )}
                value={impact ? fmtMoneyDetailed(impact.netUnitCost) : "—"}
                helperText={t(
                  "briefingsView.marginSection.platformUnitCostDialogHint",
                  "Con este margen. Plan: {{plan}}.",
                  { plan: fmtMoneyDetailed(goal.netUnitCost) }
                )}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Box>
          )}

          <Divider />

          {/* Preview: margen actual vs. cómo quedaría */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t("briefingsView.marginSection.previewTitle", "Impact preview")}
          </Typography>
          {!formValid ? (
            <Typography variant="caption" color="text.secondary">
              {t(
                "briefingsView.marginSection.previewHint",
                "Fill in dates and a valid margin (0–100) to preview the impact."
              )}
            </Typography>
          ) : isPreviewing ? (
            <Typography variant="caption" color="text.secondary">
              {t("briefingsView.marginSection.previewLoading", "Calculating…")}
            </Typography>
          ) : previewResp?.data ? (
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <MiniTimeline
                title={t(
                  "briefingsView.marginSection.previewCurrent",
                  "Current margin"
                )}
                periods={previewResp.data.current}
              />
              <MiniTimeline
                title={t(
                  "briefingsView.marginSection.previewUpdated",
                  "Updated margin"
                )}
                periods={previewResp.data.updated}
              />
            </Stack>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.cancel", "Cancel")}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={isLoading}
        >
          {t("common.save", "Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssociatedCampaignMarginDialog;
